import React, { useEffect, useState } from "react";
import Label from "../../../components/Label";
import BarChart from "../../../components/D3Charts/BarChart";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal } from "react-bootstrap";
import Icon from "../../../components/Icon";
import axios from "axios";
import { truncate } from "lodash";

const DonationGraph = () => {
  const { t } = useTranslation();

  // Bar chart state
  const [chartData, setChartData] = useState([]);
  const [chartCategories, setChartCategories] = useState([]);

  // Product owner: raw data for drill-down
  const [mosqueList, setMosqueList] = useState([]);

  // Drill-down modal state
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [modalCategories, setModalCategories] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  const activeEntity = useSelector((state) => state.adminReducer.activeEntity);
  const userType = useSelector((state) => state?.appReducer?.userType);

  const isProductOwner = userType === "product_owner";

  // Product owner: total donations per mosque
  useEffect(() => {
    if (!isProductOwner) return;

    const fetchDonationsPerMosque = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}donation/donations-per-mosque`
        );
        if (res.status === 200 && res.data?.data) {
          const data = res.data.data;
          setMosqueList(data);
          setChartCategories(data.map((item) => item.name));
          setChartData(data.map((item) => item.totalAmount));
        }
      } catch (error) {
        console.log("Donations per mosque error:", error);
      }
    };
    fetchDonationsPerMosque();
  }, [isProductOwner]);

  // Admin/Viewer: total donations per campaign (current year, auto-loaded)
  useEffect(() => {
    if (isProductOwner) return;
    if (!activeEntity?._id) return;

    const fetchCampaignSummary = async () => {
      try {
        const listRes = await axios.get(
          `${process.env.REACT_APP_API_URL}donation/formatted-list?entity_id=${activeEntity._id}`
        );
        if (listRes.status !== 200 || !listRes.data?.donations?.length) return;

        const campaigns = listRes.data.donations;
        const campaignIds = campaigns.map((c) => c._id);

        const statsRes = await axios.post(
          `${process.env.REACT_APP_API_URL}donation/donation-payments-stats`,
          {
            donationIds: campaignIds,
            year: new Date().getFullYear(),
          }
        );

        if (statsRes.status === 200 && statsRes.data?.stats) {
          const stats = statsRes.data.stats;
          const categories = [];
          const data = [];

          campaigns.forEach((campaign) => {
            const monthlyStats = stats[campaign._id] || [];
            const total = monthlyStats.reduce(
              (sum, item) => sum + (item.totalAmount || 0),
              0
            );
            categories.push(truncate(campaign.title, { length: 20 }));
            data.push(total);
          });

          setChartCategories(categories);
          setChartData(data);
        }
      } catch (error) {
        console.log("Campaign summary error:", error);
      }
    };
    fetchCampaignSummary();
  }, [activeEntity, isProductOwner]);

  // Drill-down: fetch campaign breakdown for a mosque
  const handleBarClick = async (index) => {
    if (!isProductOwner) return;

    const mosque = mosqueList[index];
    if (!mosque) return;

    setModalTitle(mosque.name);
    setShowModal(true);
    setModalLoading(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}donation/donations-per-campaign?entity_id=${mosque.entityId}`
      );

      if (res.status === 200 && res.data?.data?.length) {
        const data = res.data.data;
        setModalCategories(data.map((item) => truncate(item.title, { length: 25 })));
        setModalData(data.map((item) => item.totalAmount));
      } else {
        setModalCategories([]);
        setModalData([]);
      }
    } catch (error) {
      console.log("Drill-down error:", error);
      setModalCategories([]);
      setModalData([]);
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <div>
      <div className="plan-members">
        <Label variant="text1">
          {t("Donation Overview")}
        </Label>
        <Label variant="text5" color="#7E7E7E">
          {isProductOwner
            ? t("Total Donation Amount Per Mosque")
            : t("Total Donation Amount Per Campaign") + ` (${new Date().getFullYear()})`}
        </Label>
        {isProductOwner && chartData.length > 0 && (
          <Label variant="text5" color="#9197B3" style={{ fontSize: "12px", marginTop: "4px" }}>
            {t("Click on a bar to view campaign breakdown")}
          </Label>
        )}
        <div className="white_box" style={{ height: "100%", paddingBottom: "20px" }}>
          <div style={{ height: "100%" }}>
            {chartData.length > 0 ? (
              <BarChart
                name={t("Amount")}
                data={chartData}
                categories={chartCategories}
                onBarClick={isProductOwner ? handleBarClick : undefined}
              />
            ) : (
              <div className="d-flex justify-content-center align-items-center h-100">
                {t("No data")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drill-down Modal: Campaign breakdown per mosque */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        className="donation-drilldown-modal"
      >
        <Modal.Header>
          <Modal.Title>
            {modalTitle} — {t("Campaign Breakdown")}
          </Modal.Title>
          <button
            type="button"
            className="close"
            aria-label="Close"
            onClick={() => setShowModal(false)}
          >
            <span aria-hidden="true">
              <Icon iconName="modalClose" />
            </span>
          </button>
        </Modal.Header>
        <Modal.Body>
          {modalLoading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
              {t("Loading...")}
            </div>
          ) : modalData.length > 0 ? (
            <BarChart
              name={t("Amount")}
              data={modalData}
              categories={modalCategories}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
              {t("No campaign data available")}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DonationGraph;
